import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Award, Sparkles, Package, Globe, Users, Rocket, Send, X, MapPin, Clock, TrendingUp, ArrowRight } from "lucide-react";
import { useLang } from "../contexts/LangContext";

const ICONS = { award: Award, sparkles: Sparkles, package: Package, globe: Globe, users: Users, rocket: Rocket };
const BACKEND = process.env.REACT_APP_BACKEND_URL;
const IMG = (slug) => `${BACKEND}/api/static/tiles/${slug}.png`;

const GALLERY = [
  { slug: "franchising_storefront", title: "Storefront", caption: "Vetrina d'angolo nei centri storici" },
  { slug: "franchising_interior_wine", title: "Wine Library", caption: "1.200+ etichette curate, illuminazione editoriale" },
  { slug: "franchising_food_display", title: "Gourmet Display", caption: "Pasta artigianale, parmigiano, EVO, conserve" },
  { slug: "franchising_gift_corner", title: "Gift Box Corner", caption: "Box regalo brandizzati a marchio Amazonico 3000" },
];

function RequestModal({ onClose, onSent }) {
  const [form, setForm] = useState({
    full_name: "", email: "", phone: "", city: "", country: "IT",
    has_premises: false, available_capital: "", business_experience: "", motivation: "", consent: false,
  });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent) { toast.error("Consenso privacy richiesto"); return; }
    setBusy(true);
    try {
      await api.post("/franchising/request", form);
      toast.success("Richiesta inviata · Ti ricontatteremo entro 48h");
      onSent();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore invio");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="franc-modal" className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-white text-black max-w-2xl w-full max-h-[90vh] overflow-y-auto border-2 border-[#C9A961] rounded-sm">
        <div className="p-6 md:p-8 border-b flex items-start justify-between gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-[0.4em] text-[#C9A961] mb-2">Richiedi informazioni</div>
            <h2 className="editorial-heading text-2xl md:text-3xl text-[#7A1C2B]">Apri il tuo Amazonico 3000</h2>
            <p className="text-sm text-gray-600 mt-2">Compila il form, ti ricontatteremo entro 48h con un dossier dettagliato.</p>
          </div>
          <button data-testid="franc-modal-close" onClick={onClose} className="text-gray-500 hover:text-black"><X className="h-5 w-5" /></button>
        </div>

        <form onSubmit={submit} className="p-6 md:p-8 space-y-4" data-testid="franc-form">
          <div className="grid md:grid-cols-2 gap-4">
            <div><label className="text-xs font-bold uppercase tracking-wider">Nome e cognome *</label>
              <Input data-testid="fr-name" required value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} className="mt-1" /></div>
            <div><label className="text-xs font-bold uppercase tracking-wider">Email *</label>
              <Input data-testid="fr-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1" /></div>
            <div><label className="text-xs font-bold uppercase tracking-wider">Telefono *</label>
              <Input data-testid="fr-phone" required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1" /></div>
            <div><label className="text-xs font-bold uppercase tracking-wider">Città di interesse *</label>
              <Input data-testid="fr-city" required value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} placeholder="Es. Milano, Firenze, Lugano" className="mt-1" /></div>
            <div><label className="text-xs font-bold uppercase tracking-wider">Capitale disponibile</label>
              <Select value={form.available_capital || "_"} onValueChange={(v) => setForm({ ...form, available_capital: v === "_" ? "" : v })}>
                <SelectTrigger data-testid="fr-capital" className="mt-1"><SelectValue placeholder="Seleziona..." /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="_">—</SelectItem>
                  <SelectItem value="30-50k">€30.000 – €50.000</SelectItem>
                  <SelectItem value="50-100k">€50.000 – €100.000</SelectItem>
                  <SelectItem value="100-200k">€100.000 – €200.000</SelectItem>
                  <SelectItem value="200k+">Oltre €200.000</SelectItem>
                </SelectContent>
              </Select></div>
            <div className="flex items-end">
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input data-testid="fr-premises" type="checkbox" checked={form.has_premises} onChange={(e) => setForm({ ...form, has_premises: e.target.checked })} />
                <span>Dispongo già di un locale</span>
              </label>
            </div>
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wider">Esperienza precedente</label>
            <Textarea data-testid="fr-experience" rows={2} value={form.business_experience} onChange={(e) => setForm({ ...form, business_experience: e.target.value })} placeholder="Hai esperienza nella ristorazione, retail, food&wine? Raccontaci..." className="mt-1" />
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wider">Perché Amazonico 3000?</label>
            <Textarea data-testid="fr-motivation" rows={2} value={form.motivation} onChange={(e) => setForm({ ...form, motivation: e.target.value })} placeholder="La tua visione, le tue aspettative" className="mt-1" />
          </div>
          <label className="flex items-start gap-3 text-xs text-gray-600 cursor-pointer">
            <input data-testid="fr-consent" type="checkbox" checked={form.consent} onChange={(e) => setForm({ ...form, consent: e.target.checked })} className="mt-1" />
            <span>Accetto la <a href="/privacy" target="_blank" rel="noreferrer" className="underline">privacy policy</a> e autorizzo Amazonico 3000 a contattarmi per il progetto franchising.</span>
          </label>
          <div className="flex gap-3 pt-3 border-t">
            <Button data-testid="fr-submit" type="submit" disabled={busy} className="bg-[#7A1C2B] hover:bg-[#5a0e1c] text-white uppercase tracking-wider font-bold flex-1">
              {busy ? "Invio..." : "Invia richiesta"}
            </Button>
            <Button type="button" variant="outline" onClick={onClose}>Annulla</Button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function Franchising() {
  const { t } = useLang();
  const [info, setInfo] = useState({ pillars: [], stats: {} });
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    api.get("/franchising/info").then((r) => setInfo(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="franchising-page" className="bg-[#050505] text-white min-h-screen">
      {/* HERO */}
      <section className="relative border-b border-white/10 overflow-hidden">
        <img src={IMG("franchising_storefront")} alt="Boutique Amazonico 3000" className="absolute inset-0 w-full h-full object-cover opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/65 via-black/30 to-black/30" />
        <div className="relative container-xl py-20 md:py-32">
          <div className="max-w-3xl">
            <div className="text-[11px] uppercase tracking-[0.5em] text-[#C9A961] mb-5 flex items-center gap-3">
              <Award className="h-3.5 w-3.5" /> {t("franchising.hero_eyebrow")}
            </div>
            <h1 className="editorial-heading text-5xl md:text-7xl leading-[0.95]">
              {t("page.franchising.title")}
            </h1>
            <p className="mt-6 text-white/80 text-lg max-w-2xl">
              {t("franchising.hero_subtitle")}
            </p>
            <div className="mt-9 flex flex-wrap gap-3">
              <Button data-testid="franc-cta-hero" onClick={() => setShowModal(true)} className="bg-[#C9A961] hover:bg-white text-black uppercase tracking-[0.2em] text-xs font-bold px-8 py-6">
                {t("franchising.request_info")}
              </Button>
              <a href="#points" className="inline-flex items-center gap-2 px-7 py-3 border border-white/30 hover:border-[#C9A961] text-white uppercase tracking-[0.2em] text-xs font-bold transition-colors">
                Scopri i punti di forza <ArrowRight className="h-3.5 w-3.5" />
              </a>
            </div>

            {info.stats && (
              <div className="mt-12 flex flex-wrap gap-8 text-sm">
                <div><div className="text-3xl font-bold text-[#C9A961]">{info.stats.stores_target_2026 || 12}</div><div className="text-[10px] uppercase tracking-[0.3em] text-white/50 mt-1">Store target 2026</div></div>
                <div><div className="text-3xl font-bold text-[#C9A961]">{info.stats.average_payback_months || 28} <span className="text-base">mesi</span></div><div className="text-[10px] uppercase tracking-[0.3em] text-white/50 mt-1">Payback medio</div></div>
                <div><div className="text-3xl font-bold text-[#C9A961]">{info.stats.operativity_days || 30} <span className="text-base">gg</span></div><div className="text-[10px] uppercase tracking-[0.3em] text-white/50 mt-1">Operativi in</div></div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* GALLERY */}
      <section className="container-xl py-16 md:py-20">
        <div className="mb-10">
          <div className="text-[11px] uppercase tracking-[0.4em] text-[#C9A961] mb-3">Il format</div>
          <h2 className="editorial-heading text-4xl md:text-5xl">Uno spazio progettato per emozionare.</h2>
          <p className="text-white/60 mt-3 max-w-2xl">Vetrina, wine library, gourmet shelf e gift corner: ogni elemento del design è già definito, replicabile e brand-coherent.</p>
        </div>
        <div className="grid md:grid-cols-2 gap-5">
          {GALLERY.map((g, i) => (
            <div key={g.slug} data-testid={`franc-gallery-${g.slug}`} className={`relative group overflow-hidden ${i === 0 || i === 3 ? "md:row-span-1" : ""}`}>
              <div className="aspect-[16/10] overflow-hidden bg-[#0a0a0a]">
                <img src={IMG(g.slug)} alt={g.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-5">
                <div className="text-[10px] uppercase tracking-[0.4em] text-[#C9A961]">{g.title}</div>
                <div className="text-sm text-white mt-1">{g.caption}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* PILLARS */}
      <section id="points" className="border-y border-white/10 bg-[#0a0a0a]">
        <div className="container-xl py-16 md:py-20">
          <div className="mb-12 text-center">
            <div className="text-[11px] uppercase tracking-[0.4em] text-[#C9A961] mb-3">Perché aprire un Amazonico 3000</div>
            <h2 className="editorial-heading text-4xl md:text-5xl">6 punti di forza.</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {info.pillars.map((p) => {
              const Icon = ICONS[p.icon] || Award;
              return (
                <div key={p.id} data-testid={`pillar-${p.id}`} className="bg-[#050505] border border-white/10 p-7 hover:border-[#C9A961]/60 transition-colors">
                  <div className="w-12 h-12 rounded-full bg-[#C9A961]/15 flex items-center justify-center mb-4">
                    <Icon className="h-5 w-5 text-[#C9A961]" />
                  </div>
                  <h3 className="font-bold text-lg leading-tight">{p.title}</h3>
                  <p className="mt-3 text-sm text-white/60 leading-relaxed">{p.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA FINALE */}
      <section className="container-xl py-20 text-center">
        <div className="max-w-2xl mx-auto">
          <h2 className="editorial-heading text-4xl md:text-5xl">Pronto a iniziare?</h2>
          <p className="text-white/60 mt-4 text-lg">Compila il form: ti ricontatteremo entro 48h con un dossier dettagliato e una call preliminare.</p>
          <Button data-testid="franc-cta-bottom" onClick={() => setShowModal(true)} className="mt-8 bg-[#C9A961] hover:bg-white text-black uppercase tracking-[0.2em] text-xs font-bold px-10 py-7">
            {t("franchising.request_info")}
          </Button>
          <div className="mt-6 flex justify-center gap-8 text-sm text-white/40">
            <div className="flex items-center gap-2"><Clock className="h-4 w-4" />Risposta in 48h</div>
            <div className="flex items-center gap-2"><MapPin className="h-4 w-4" />Italia ed Europa</div>
            <div className="flex items-center gap-2"><TrendingUp className="h-4 w-4" />ROI medio 28 mesi</div>
          </div>
        </div>
      </section>

      {showModal && <RequestModal onClose={() => setShowModal(false)} onSent={() => setShowModal(false)} />}
    </div>
  );
}
